import { create } from 'zustand';
import { Model } from '../types';
import { modelApi } from '../services/modelApi';

const PROVIDER_VISIBILITY_KEY = 'bongai_provider_visibility';

const DEFAULT_PROVIDER_VISIBILITY: Record<string, boolean> = {
  gemini: true,
  runninghub: true,
  grsai: true,
  comfyui: true,
  falai: true,
  agnes: true,
};

const loadProviderVisibility = (): Record<string, boolean> => {
  try {
    const stored = localStorage.getItem(PROVIDER_VISIBILITY_KEY);
    if (stored) return { ...DEFAULT_PROVIDER_VISIBILITY, ...JSON.parse(stored) };
  } catch {}
  return DEFAULT_PROVIDER_VISIBILITY;
};

interface ModelState {
  models: Model[];
  isLoading: boolean;
  error: string | null;
  providerVisibility: Record<string, boolean>;
  loadModels: () => Promise<void>;
  getModelsByMode: (mode: string) => Model[];
  getModelById: (id: string) => Model | undefined;
  setProviderVisibility: (provider: string, visible: boolean) => void;
}

let loadModelsPromise: Promise<void> | null = null;

export const useModelStore = create<ModelState>((set, get) => ({
  models: [],
  isLoading: false,
  error: null,
  providerVisibility: loadProviderVisibility(),
  
  loadModels: async () => {
    if (loadModelsPromise) return loadModelsPromise;
    
    loadModelsPromise = (async () => {
      set({ isLoading: true, error: null });
      try {
        const models = await modelApi.getModels();
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        set({ models, isLoading: false });
      } catch (e: any) {
        set({ error: e.message, isLoading: false });
        console.error('Failed to load models:', e);
      } finally {
        loadModelsPromise = null;
      }
    })();
    
    return loadModelsPromise;
  },
  
  getModelsByMode: (mode) => {
    const state = get();
    return state.models.filter((m) => {
      if (!m.supportedModes.includes(mode)) return false;
      const visible = state.providerVisibility[m.backend];
      return visible !== false;
    });
  },
  
  getModelById: (id) => {
    const state = get();
    return state.models.find((m) => m.id === id);
  },

  setProviderVisibility: (provider, visible) => {
    set((state) => {
      const newVisibility = { ...state.providerVisibility, [provider]: visible };
      localStorage.setItem(PROVIDER_VISIBILITY_KEY, JSON.stringify(newVisibility));
      return { providerVisibility: newVisibility };
    });
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}